import { type SigninSilentArgs, type User, UserManager } from 'oidc-client-ts';
import { impersonationService } from '@/services/impersonation.service';

const COGNITO_ISSUER = import.meta.env.VITE_COGNITO_ISSUER;
const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;
const COGNITO_REDIRECT_URI = import.meta.env.VITE_COGNITO_REDIRECT_URI;

export const CONTROL_CENTER_COGNITO_CLIENT_ID = (
  import.meta.env.VITE_CONTROL_CENTER_COGNITO_CLIENT_ID as string | undefined
)?.trim();

export const cognitoUserManagerSettings = {
  authority: COGNITO_ISSUER,
  client_id: COGNITO_CLIENT_ID,
  redirect_uri: COGNITO_REDIRECT_URI,
  response_type: 'code' as const,
  scope: import.meta.env.VITE_COGNITO_SCOPE ?? 'openid email profile',
  automaticSilentRenew: true,
  accessTokenExpiringNotificationTimeInSeconds: 60,
};

/**
 * Impersonation handoff tokens are issued by the Control Center Cognito app client.
 * Cognito binds refresh_token to that client_id, so silent renew must use it — not MPA's.
 */
const installImpersonationAwareSilentRenew = ({ userManager }: { userManager: UserManager }): void => {
  const originalSigninSilent = userManager.signinSilent.bind(userManager);

  userManager.signinSilent = async (args: SigninSilentArgs = {}): Promise<User | null> => {
    if (!impersonationService.getSession().isImpersonating) {
      return originalSigninSilent(args);
    }

    if (!CONTROL_CENTER_COGNITO_CLIENT_ID) {
      throw new Error('VITE_CONTROL_CENTER_COGNITO_CLIENT_ID is required to refresh tokens during impersonation');
    }

    return originalSigninSilent({
      ...args,
      extraTokenParams: {
        ...args.extraTokenParams,
        client_id: CONTROL_CENTER_COGNITO_CLIENT_ID,
      },
    });
  };
};

let cognitoUserManagerInstance: UserManager | null = null;

export const getCognitoUserManager = (): UserManager => {
  if (!cognitoUserManagerInstance) {
    cognitoUserManagerInstance = new UserManager(cognitoUserManagerSettings);
    installImpersonationAwareSilentRenew({ userManager: cognitoUserManagerInstance });
  }
  return cognitoUserManagerInstance;
};

export const cognitoUserManager = getCognitoUserManager();

export const getCognitoUserInfoUrl = (): string => {
  return `${COGNITO_DOMAIN}/oauth2/userInfo`;
};
